import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Button from "./common/Button";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "./common/Card";
import config from "../config";
import { Calendar, FileText, Shield, MessageCircle, Users, Target, Heart, ChevronDown, Zap, Check } from "lucide-react"

import * as utils from "../utils/utils";
import useBrandTranslation from "../hooks/useBrandTranslation";
import LanguageSwitcher from "./common/LanguageSwitcher";
import esWeb from "../i18n/locales/es/web";

function DownloadAppDialog({ open, onCancel, onLearnMore, brandName }) {
  const { t } = useBrandTranslation();

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h2 className="text-2xl font-semibold text-gray-900">
          {t("web.downloadApp.title", "Descargá la app")}
        </h2>
        <p className="mt-3 text-gray-600">
          {t(
            "web.downloadApp.body",
            "Llevá {{brand}} siempre con vos. Gestioná tus turnos desde la app y mantente al día estés donde estés.",
            { brand: brandName }
          )}
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
          >
            {t("web.downloadApp.cancel", "Cancelar")}
          </button>
          <Button
            className="px-4 py-2 text-sm font-semibold"
            onClick={onLearnMore}
          >
            {t("web.downloadApp.learnMore", "Saber más")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Web() {
  if (config.brand === "ARISTAS" || config.brand === "DEFAULT") {
    return <Aristas />;
  } else if (config.brand === "PELUCA") {
    return <Peluca />;
  } else if (config.brand === "MANICURA") {
    return <Manicura />;
  } else if (config.brand === "MASCOTAS") {
    return <Mascotas />;
  } else if (config.brand === "BINAPSIS") {
    return <Binapsis />;
  } else {
    return <div>Configuración de marca no soportada.</div>;
  }
}

export function Aristas() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDownloadDialog, setShowDownloadDialog] = useState(true);
  const isMobile = window.screen.width < 768;

  const iosStandalone = window.navigator.standalone === true;
  const standalone = window.matchMedia("(display-mode: standalone)").matches;
  const fullscreen = window.matchMedia("(display-mode: fullscreen)").matches;
  const minimalUI = window.matchMedia("(display-mode: minimal-ui)").matches;
  const isRunningAsInstalledPWA = iosStandalone || standalone || fullscreen || minimalUI;

  return (
    <main className="min-h-screen bg-white text-gray-800">

      {!isRunningAsInstalledPWA && (
        <DownloadAppDialog
          open={showDownloadDialog}
          brandName={config.brand}
          onCancel={() => setShowDownloadDialog(false)}
          onLearnMore={() => {
            setShowDownloadDialog(false);
            window.location.assign("/install");
          }}
        />
      )}
      <header
        className={`text-white px-6 py-4 pt-6 shadow-md sticky top-0 z-50 bg-[${config.theme.colors.primaryColor}]`}
      >
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
          <div className="flex items-center justify-center bg-white rounded-full h-14 w-14">
                <img
                  src={process.env.PUBLIC_URL + utils.getBrandLogo()}
                  alt={config.brand}
                  className="h-10 w-10 object-contain"
                />
              </div>
            <h1 className="text-2xl font-bold">{config.brand}</h1>
          </div>
          {isMobile ? (
            <>
              <button
                className="whitespace-nowrap rounded-lg text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 hover:bg-slate-100 hover:text-slate-900 h-10 w-10 flex items-center justify-center"
                type="button"
                id="radix-:R2pjta:"
                aria-haspopup="menu"
                aria-expanded="false"
                data-state="closed"
                onClick={() => {
                  setMenuOpen(!menuOpen);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-menu h-6 w-6"
                >
                  <line x1="4" x2="20" y1="12" y2="12"></line>
                  <line x1="4" x2="20" y1="6" y2="6"></line>
                  <line x1="4" x2="20" y1="18" y2="18"></line>
                </svg>
              </button>
              {menuOpen && (
                <div
                  className="absolute right-2 z-10 w-48 mr-[16px] mt-[237px] origin-top-right bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="radix-:R2pjta:"
                  tabIndex="-1"
                >
                  <div className="px-1 py-1" role="none">
                    <a
                      href="#features"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                      tabIndex="-1"
                      id="radix-:R2pjta:0"
                      onClick={() => setMenuOpen(false)}
                    >
                      Características
                    </a>
                    <a
                      href="#about"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                      tabIndex="-1"
                      id="radix-:R2pjta3:0"
                      onClick={() => setMenuOpen(false)}
                    >
                      Nosotros
                    </a>
                    <a
                      href="#contact"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                      tabIndex="-1"
                      id="radix-:R2pj2ta:0"
                      onClick={() => setMenuOpen(false)}
                    >
                      Contacto
                    </a>
                    <a
                      href="/register"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                      tabIndex="-1"
                      id="radix-:R2dpjta:0"
                      onClick={() => setMenuOpen(false)}
                    >
                      Registro
                    </a>
                    <a
                      href="/login"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                      tabIndex="-1"
                      id="radix-:R2pjcta:0"
                      onClick={() => setMenuOpen(false)}
                    >
                      Acceso
                    </a>
                  </div>
                </div>
              )}
            </>
          ) : (
            <nav className="space-x-4">
              <a href="#features" className="hover:underline">
                Características
              </a>
              <a href="#about" className="hover:underline">
                Nosotros
              </a>
              <a href="#contact" className="hover:underline">
                Contacto
              </a>
              <a href="/register" className="hover:underline">
                Registro
              </a>
              <a href="/login" className="hover:underline">
                Acceso
              </a>
            </nav>
          )}
        </div>
      </header>

      <section className="bg-blue-100 py-20 text-center px-4">
        <h2 className="text-4xl font-bold mb-4">
          La plataforma integral para profesionales de la salud
        </h2>
        <p className="text-lg mb-6 max-w-2xl mx-auto">
          Gestioná turnos, historias clínicas, pacientes y mucho más desde un
          solo lugar. Rápido, seguro y accesible.
        </p>

        <Button
          className="text-lg flex gap-2 items-center mx-auto"
          onClick={() =>
            window.location.assign(
              "https://api.whatsapp.com/send?phone=5491158888423&text=Hola,%20quiero%20saber%20mas%20sobre%Aristas"
            )
          }
        >
          <p>Solicitá una demo</p>
          <svg
            stroke="currentColor"
            fill="currentColor"
            strokeWidth="0"
            viewBox="0 0 448 512"
            className="text-xl"
            height="1em"
            width="1em"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"></path>
          </svg>
        </Button>
      </section>

      {config.brand === "PELUCA" && (
        <section id="features" className="py-20 bg-white px-4">
          <div className="max-w-6xl mx-auto w-full text-center">
            <div>
              <h3 className="text-xl font-semibold mb-2">
                Turnos para peluqueria
              </h3>
              <p>
                Organizá tu agenda con facilidad. Confirmaciones automáticas por
                WhatsApp.
              </p>
            </div>
          </div>
        </section>
      )}

      {config.brand === "DEFAULT" && (
        <section id="features" className="py-20 bg-white px-4">
          <div className="max-w-6xl mx-auto w-full text-center">
            <div>
              <h3 className="text-xl font-semibold mb-2">
                Gestión de Turnos para tu negocio
              </h3>
              <p>
                Organizá tu agenda con facilidad. Confirmaciones automáticas por
                WhatsApp.
              </p>
            </div>
          </div>
        </section>
      )}

      {config.brand === "ARISTAS" && (
        <section id="features" className="py-20 bg-white px-4">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <h3 className="text-xl font-semibold mb-2">Gestión de Turnos</h3>
              <p>
                Organizá tu agenda con facilidad. Confirmaciones automáticas por
                WhatsApp.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">
                Historias Clínicas Digitales
              </h3>
              <p>
                Accedé a los antecedentes de tus pacientes en cualquier momento,
                desde cualquier lugar.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">
                Privacidad y Seguridad
              </h3>
              <p>
                Cumplimos con los más altos estándares para resguardar los datos
                de tus pacientes.
              </p>
            </div>
          </div>
        </section>
      )}

      <section id="about" className="bg-gray-100 py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-2xl font-bold mb-4">¿Quiénes somos?</h3>
          <p>
            Somos un equipo de profesionales de la salud y la tecnología con el
            objetivo de modernizar la gestión médica en Latinoamérica.
          </p>
        </div>
      </section>

      <section id="contact" className="pt-20 pb-10 px-4">
        <div className="max-w-xl mx-auto text-center">
          <h3 className="text-2xl font-bold mb-4">Contacto</h3>
          <p className="mb-2">
            <a
              href={`mailto:${config.email}`}
              className="text-blue-600 underline"
            >
              {config.email}
            </a>
          </p>
          <p className="mb-2">
            <a
              href={`tel:+${config.phone}`}
              className="text-blue-600 underline"
            >
              {config.phone}
            </a>
          </p>
          <p className="mb-2">{config.address}</p>
          <div className="mt-6">
            <Button
              className="text-lg flex gap-2 items-center mx-auto"
              onClick={() =>
                window.location.assign(
                  "https://api.whatsapp.com/send?phone=5491158888423&text=Hola,%20quiero%20saber%20mas%20sobre%Aristas"
                )
              }
            >
              <p>Solicitá una demo</p>
              <svg
                stroke="currentColor"
                fill="currentColor"
                strokeWidth="0"
                viewBox="0 0 448 512"
                className="text-xl"
                height="1em"
                width="1em"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"></path>
              </svg>
            </Button>
          </div>

          <div className="flex justify-center gap-4 mt-10">
            <a
              href={config.facebook}
              target="_blank"
              className="text-blue-600 underline"
            >
              Facebook
            </a>
            <a
              href={config.instagram}
              target="_blank"
              className="text-blue-600 underline"
            >
              Instagram
            </a>
            <a
              href={config.twitter}
              target="_blank"
              className="text-blue-600 underline"
            >
              Twitter
            </a>
          </div>
        </div>
      </section>

      <footer
        className={`text-white text-center p-4 bg-[${config.theme.colors.primaryColor}]`}
      >
        <p>
          &copy; {new Date().getFullYear()} {config.brand}. Todos los derechos
          reservados.
        </p>
      </footer>
    </main>
  );
}

export function Peluca() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDownloadDialog, setShowDownloadDialog] = useState(true);
  const isMobile = window.screen.width < 768;

  const iosStandalone = window.navigator.standalone === true;
  const standalone = window.matchMedia("(display-mode: standalone)").matches;
  const fullscreen = window.matchMedia("(display-mode: fullscreen)").matches;
  const minimalUI = window.matchMedia("(display-mode: minimal-ui)").matches;
  const isRunningAsInstalledPWA = iosStandalone || standalone || fullscreen || minimalUI;

  return (
    <main className="min-h-screen bg-white text-gray-800">
      {!isRunningAsInstalledPWA && (
        <DownloadAppDialog
          open={showDownloadDialog}
          brandName={config.brand}
          onCancel={() => setShowDownloadDialog(false)}
          onLearnMore={() => {
            setShowDownloadDialog(false);
            window.location.assign("/install");
          }}
        />
      )}
      <header
        className={`text-white px-6 py-4 pt-6 shadow-md sticky top-0 z-50 bg-[${config.theme.colors.primaryColor}]`}
      >
        <div className="max-w-6xl mx-auto flex justify-between items-center">
        <div className="flex items-center gap-2">
        <div className="flex items-center justify-center bg-white rounded-full h-14 w-14">
                <img
                  src={process.env.PUBLIC_URL + utils.getBrandLogo()}
                  alt={config.brand}
                  className="h-10 w-10 object-contain"
                />
              </div>
            <h1 className="text-2xl font-bold">{config.brand}</h1>
          </div>
          {isMobile ? (
            <>
              <button
                className="whitespace-nowrap rounded-lg text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 hover:bg-slate-100 hover:text-slate-900 h-10 w-10 flex items-center justify-center"
                type="button"
                id="peluca-menu"
                aria-haspopup="menu"
                aria-expanded={menuOpen ? "true" : "false"}
                data-state={menuOpen ? "open" : "closed"}
                onClick={() => setMenuOpen(!menuOpen)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-menu h-6 w-6"
                >
                  <line x1="4" x2="20" y1="12" y2="12"></line>
                  <line x1="4" x2="20" y1="6" y2="6"></line>
                  <line x1="4" x2="20" y1="18" y2="18"></line>
                </svg>
              </button>
              {menuOpen && (
                <div
                  className="absolute right-2 z-10 w-48 mr-[16px] mt-[237px] origin-top-right bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="peluca-menu"
                  tabIndex="-1"
                >
                  <div className="px-1 py-1" role="none">
                    <a
                      href="#features"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                      tabIndex="-1"
                      id="peluca-menu-0"
                      onClick={() => setMenuOpen(false)}
                    >
                      Servicios
                    </a>
                    <a
                      href="#about"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                      tabIndex="-1"
                      id="peluca-menu-1"
                      onClick={() => setMenuOpen(false)}
                    >
                      Nosotros
                    </a>
                    <a
                      href="#contact"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                      tabIndex="-1"
                      id="peluca-menu-2"
                      onClick={() => setMenuOpen(false)}
                    >
                      Contacto
                    </a>
                    <a
                      href="/register"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                      tabIndex="-1"
                      id="peluca-menu-3"
                      onClick={() => setMenuOpen(false)}
                    >
                      Registro
                    </a>
                    <a
                      href="/login"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                      tabIndex="-1"
                      id="peluca-menu-4"
                      onClick={() => setMenuOpen(false)}
                    >
                      Acceso
                    </a>
                  </div>
                </div>
              )}
            </>
          ) : (
            <nav className="space-x-4">
              <a href="#features" className="hover:underline">
                Servicios
              </a>
              <a href="#about" className="hover:underline">
                Nosotros
              </a>
              <a href="#contact" className="hover:underline">
                Contacto
              </a>
              <a href="/register" className="hover:underline">
                Registro
              </a>
              <a href="/login" className="hover:underline">
                Acceso
              </a>
            </nav>
          )}
        </div>
      </header>

      <section className="bg-blue-100 py-20 text-center px-4">
        <h2 className="text-4xl font-bold mb-4">
          La solución para modernizar tu peluquería
        </h2>
        <p className="text-lg mb-6 max-w-2xl mx-auto">
          Gestioná turnos, clientes y servicios desde una sola plataforma. Mejorá la experiencia de tus clientes y optimizá tu tiempo.
        </p>

        <Button
          className="text-lg flex gap-2 items-center mx-auto"
          onClick={() =>
            window.location.assign(
              "https://api.whatsapp.com/send?phone=5491158888423&text=Hola,%20quiero%20agendar%20un%20turno%20en%20PELUKA"
            )
          }
        >
          <p>Reservá tu turno</p>
          <svg
            stroke="currentColor"
            fill="currentColor"
            strokeWidth="0"
            viewBox="0 0 448 512"
            className="text-xl"
            height="1em"
            width="1em"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"></path>
          </svg>
        </Button>
      </section>

      <section id="features" className="py-20 bg-white px-4">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          <div>
            <h3 className="text-xl font-semibold mb-2">
              Agenda de Turnos Online
            </h3>
            <p>
              Permití que tus clientes reserven sus turnos fácilmente en línea y recibí confirmaciones automáticas por WhatsApp.
            </p>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-2">
              Gestión de Servicios y Staff
            </h3>
            <p>
              Organizá tus servicios (corte, color, peinados y más) y asigná empleados o profesionales a cada uno.
            </p>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-2">
              Historial de Clientes
            </h3>
            <p>
              Accedé a la información y preferencias de tus clientes para personalizar su experiencia en cada visita.
            </p>
          </div>
        </div>
      </section>

      <section id="about" className="bg-gray-100 py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-2xl font-bold mb-4">¿Quiénes somos?</h3>
          <p>
            En <b>PELUCA</b> somos un equipo apasionado por el mundo de la belleza y la tecnología, enfocados en brindar la mejor experiencia tanto a peluqueros como a sus clientes.
          </p>
          <p className="mt-4">
            Nuestra misión es ayudarte a hacer crecer tu peluquería, simplificando la gestión diaria y conectando mejor con tus clientes.
          </p>
        </div>
      </section>

      <section id="contact" className="pt-20 pb-10 px-4">
        <div className="max-w-xl mx-auto text-center">
          <h3 className="text-2xl font-bold mb-4">Contacto</h3>
          <p className="mb-2">
            <a
              href={`mailto:${config.email}`}
              className="text-blue-600 underline"
            >
              {config.email}
            </a>
          </p>
          <p className="mb-2">
            <a
              href={`tel:+${config.phone}`}
              className="text-blue-600 underline"
            >
              {config.phone}
            </a>
          </p>
          <p className="mb-2">{config.address}</p>
          <div className="mt-6">
            <Button
              className="text-lg flex gap-2 items-center mx-auto"
              onClick={() =>
                window.location.assign(
                  "https://api.whatsapp.com/send?phone=5491158888423&text=Hola,%20quiero%20agendar%20un%20turno%20en%20PELUKA"
                )
              }
            >
              <p>Reservá tu turno</p>
              <svg
                stroke="currentColor"
                fill="currentColor"
                strokeWidth="0"
                viewBox="0 0 448 512"
                className="text-xl"
                height="1em"
                width="1em"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"></path>
              </svg>
            </Button>
          </div>
          <div className="flex justify-center gap-4 mt-10">
            <a
              href={config.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              Facebook
            </a>
            <a
              href={config.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              Instagram
            </a>
            <a
              href={config.twitter}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              Twitter
            </a>
          </div>
        </div>
      </section>

      <footer
        className={`text-white text-center p-4 bg-[${config.theme.colors.primaryColor}]`}
      >
        <p>
          &copy; {new Date().getFullYear()} PELUKA. Todos los derechos
          reservados.
        </p>
      </footer>
    </main>
  );
}

export function Mascotas() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDownloadDialog, setShowDownloadDialog] = useState(true)
  const isMobile = window.screen.width < 768;
  const facebookUrl = config.socialMedia?.facebook || config.facebook;
  const instagramUrl = config.socialMedia?.instagram || config.instagram;
  const twitterUrl = config.socialMedia?.twitter || config.twitter;

  const iosStandalone = window.navigator.standalone === true;
  const standalone = window.matchMedia("(display-mode: standalone)").matches;
  const fullscreen = window.matchMedia("(display-mode: fullscreen)").matches;
  const minimalUI = window.matchMedia("(display-mode: minimal-ui)").matches;
  const isRunningAsInstalledPWA = iosStandalone || standalone || fullscreen || minimalUI;

  return (
    <main className="min-h-screen bg-white text-gray-800">
      {!isRunningAsInstalledPWA && (
        <DownloadAppDialog
          open={showDownloadDialog}
          brandName={config.brand}
          onCancel={() => setShowDownloadDialog(false)}
          onLearnMore={() => {
            setShowDownloadDialog(false);
            window.location.assign("/install");
          }}
        />
      )}
      <header
        className={`text-white px-6 py-4 pt-6 shadow-md sticky top-0 z-50 bg-[${config.theme.colors.primaryColor}]`}
      >
        <div className="max-w-6xl mx-auto flex justify-between items-center">
        <div className="flex items-center gap-2">
        <div className="flex items-center justify-center bg-white rounded-full h-14 w-14">
                <img
                  src={process.env.PUBLIC_URL + utils.getBrandLogo()}
                  alt={config.brand}
                  className="h-10 w-10 object-contain"
                />
              </div>
            <h1 className="text-2xl font-bold">{config.brand}</h1>
          </div>
          {isMobile ? (
            <>
              <button
                className="whitespace-nowrap rounded-lg text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 hover:bg-slate-100 hover:text-slate-900 h-10 w-10 flex items-center justify-center"
                type="button"
                id="mascotas-menu"
                aria-haspopup="menu"
                aria-expanded={menuOpen ? "true" : "false"}
                data-state={menuOpen ? "open" : "closed"}
                onClick={() => setMenuOpen(!menuOpen)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-menu h-6 w-6"
                >
                  <line x1="4" x2="20" y1="12" y2="12"></line>
                  <line x1="4" x2="20" y1="6" y2="6"></line>
                  <line x1="4" x2="20" y1="18" y2="18"></line>
                </svg>
              </button>
              {menuOpen && (
                <div
                  className="absolute right-2 z-10 w-48 mr-[16px] mt-[237px] origin-top-right bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="mascotas-menu"
                  tabIndex="-1"
                >
                  <div className="px-1 py-1" role="none">
                    <a
                      href="#features"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                      tabIndex="-1"
                      id="mascotas-menu-0"
                      onClick={() => setMenuOpen(false)}
                    >
                      Soluciones
                    </a>
                    <a
                      href="#about"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                      tabIndex="-1"
                      id="mascotas-menu-1"
                      onClick={() => setMenuOpen(false)}
                    >
                      Nosotros
                    </a>
                    <a
                      href="#contact"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                      tabIndex="-1"
                      id="mascotas-menu-2"
                      onClick={() => setMenuOpen(false)}
                    >
                      Contacto
                    </a>
                    <a
                      href="/register"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                      tabIndex="-1"
                      id="mascotas-menu-3"
                      onClick={() => setMenuOpen(false)}
                    >
                      Registro
                    </a>
                    <a
                      href="/login"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                      tabIndex="-1"
                      id="mascotas-menu-4"
                      onClick={() => setMenuOpen(false)}
                    >
                      Acceso
                    </a>
                  </div>
                </div>
              )}
            </>
          ) : (
            <nav className="space-x-4">
              <a href="#features" className="hover:underline">
                Soluciones
              </a>
              <a href="#about" className="hover:underline">
                Nosotros
              </a>
              <a href="#contact" className="hover:underline">
                Contacto
              </a>
              <a href="/register" className="hover:underline">
                Registro
              </a>
              <a href="/login" className="hover:underline">
                Acceso
              </a>
            </nav>
          )}
        </div>
      </header>

      <section className="bg-green-100 py-20 text-center px-4">
        <h2 className="text-4xl font-bold mb-4">
          La plataforma perfecta para veterinarias y pet shops
        </h2>
        <p className="text-lg mb-6 max-w-2xl mx-auto">
          Administrá pacientes, turnos, stock de productos y recordatorios de
          vacunas desde un único lugar pensado para el cuidado de mascotas.
        </p>

        <Button
          className="text-lg flex gap-2 items-center mx-auto"
          onClick={() =>
            window.location.assign(
              "https://api.whatsapp.com/send?phone=5491158888423&text=Hola,%20quiero%20saber%20m%C3%A1s%20sobre%20MASCOTAS"
            )
          }
        >
          <p>Solicitá una demo</p>
          <svg
            stroke="currentColor"
            fill="currentColor"
            strokeWidth="0"
            viewBox="0 0 448 512"
            className="text-xl"
            height="1em"
            width="1em"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"></path>
          </svg>
        </Button>
      </section>

      <section id="features" className="py-20 bg-white px-4">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          <div>
            <h3 className="text-xl font-semibold mb-2">
              Historia clínica veterinaria
            </h3>
            <p>
              Registrá vacunas, tratamientos y alergias para seguir de cerca la
              salud de cada mascota.
            </p>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-2">
              Turnos y recordatorios automáticos
            </h3>
            <p>
              Reducí ausencias enviando recordatorios por WhatsApp y correo
              electrónico.
            </p>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-2">
              Gestión de tienda y servicios
            </h3>
            <p>
              Controlá tu inventario de productos, servicios de grooming y
              planes de cuidado completos.
            </p>
          </div>
        </div>
      </section>

      <section id="about" className="bg-gray-100 py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-2xl font-bold mb-4">¿Quiénes somos?</h3>
          <p>
            Somos amantes de las mascotas y la tecnología. Trabajamos con
            veterinarias y pet shops de toda Latinoamérica para brindar una
            experiencia moderna en cada visita.
          </p>
          <p className="mt-4">
            Nuestro objetivo es que tu equipo se enfoque en lo importante: el
            bienestar de cada animal que atendés.
          </p>
        </div>
      </section>

      <section id="contact" className="pt-20 pb-10 px-4">
        <div className="max-w-xl mx-auto text-center">
          <h3 className="text-2xl font-bold mb-4">Contacto</h3>
          <p className="mb-2">
            <a
              href={`mailto:${config.email}`}
              className="text-blue-600 underline"
            >
              {config.email}
            </a>
          </p>
          <p className="mb-2">
            <a
              href={`tel:+${config.phone}`}
              className="text-blue-600 underline"
            >
              {config.phone}
            </a>
          </p>
          <p className="mb-2">{config.address}</p>
          <div className="mt-6">
            <Button
              className="text-lg flex gap-2 items-center mx-auto"
              onClick={() =>
                window.location.assign(
                  "https://api.whatsapp.com/send?phone=5491158888423&text=Hola,%20quiero%20saber%20m%C3%A1s%20sobre%20MASCOTAS"
                )
              }
            >
              <p>Contactanos por WhatsApp</p>
              <svg
                stroke="currentColor"
                fill="currentColor"
                strokeWidth="0"
                viewBox="0 0 448 512"
                className="text-xl"
                height="1em"
                width="1em"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"></path>
              </svg>
            </Button>
          </div>

          <div className="flex justify-center gap-4 mt-10">
            {facebookUrl && (
              <a
                href={facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline"
              >
                Facebook
              </a>
            )}
            {instagramUrl && (
              <a
                href={instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline"
              >
                Instagram
              </a>
            )}
            {twitterUrl && (
              <a
                href={twitterUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline"
              >
                Twitter
              </a>
            )}
          </div>
        </div>
      </section>

      <footer
        className={`text-white text-center p-4 bg-[${config.theme.colors.primaryColor}]`}
      >
        <p>
          &copy; {new Date().getFullYear()} {config.brand}. Todos los derechos
          reservados.
        </p>
      </footer>
    </main>
  );
}

export function Manicura() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDownloadDialog, setShowDownloadDialog] = useState(true);
  const isMobile = window.screen.width < 768;

  const iosStandalone = window.navigator.standalone === true;
  const standalone = window.matchMedia("(display-mode: standalone)").matches;
  const fullscreen = window.matchMedia("(display-mode: fullscreen)").matches;
  const minimalUI = window.matchMedia("(display-mode: minimal-ui)").matches;
  const isRunningAsInstalledPWA = iosStandalone || standalone || fullscreen || minimalUI;


  return (
    <main className="min-h-screen bg-white text-gray-800">
      {!isRunningAsInstalledPWA && (
        <DownloadAppDialog
          open={showDownloadDialog}
          brandName={config.brand}
          onCancel={() => setShowDownloadDialog(false)}
          onLearnMore={() => {
            setShowDownloadDialog(false);
            window.location.assign("/install");
          }}
        />
      )}
      <header
        className={`text-white px-6 py-4 pt-6 shadow-md sticky top-0 z-50 bg-[${config.theme.colors.primaryColor}]`}
      >
        <div className="max-w-6xl mx-auto flex justify-between items-center">
        <div className="flex items-center gap-2">
              <div className="flex items-center justify-center bg-white rounded-full h-14 w-14">
                <img
                  src={process.env.PUBLIC_URL + utils.getBrandLogo()}
                  alt={config.brand}
                  className="h-10 w-10 object-contain"
                />
              </div>
            <h1 className="text-2xl font-bold">{config.brand}</h1>
          </div>
          {isMobile ? (
            <>
              <button
                className="whitespace-nowrap rounded-lg text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 hover:bg-slate-100 hover:text-slate-900 h-10 w-10 flex items-center justify-center"
                type="button"
                id="manicura-menu"
                aria-haspopup="menu"
                aria-expanded={menuOpen ? "true" : "false"}
                data-state={menuOpen ? "open" : "closed"}
                onClick={() => setMenuOpen(!menuOpen)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-menu h-6 w-6"
                >
                  <line x1="4" x2="20" y1="12" y2="12"></line>
                  <line x1="4" x2="20" y1="6" y2="6"></line>
                  <line x1="4" x2="20" y1="18" y2="18"></line>
                </svg>
              </button>
              {menuOpen && (
                <div
                  className="absolute right-2 z-10 w-48 mr-[16px] mt-[237px] origin-top-right bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="manicura-menu"
                  tabIndex="-1"
                >
                  <div className="px-1 py-1" role="none">
                    <a
                      href="#features"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                      tabIndex="-1"
                      id="manicura-menu-0"
                      onClick={() => setMenuOpen(false)}
                    >
                      Servicios
                    </a>
                    <a
                      href="#about"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                      tabIndex="-1"
                      id="manicura-menu-1"
                      onClick={() => setMenuOpen(false)}
                    >
                      Nosotros
                    </a>
                    <a
                      href="#contact"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                      tabIndex="-1"
                      id="manicura-menu-2"
                      onClick={() => setMenuOpen(false)}
                    >
                      Contacto
                    </a>
                    <a
                      href="/register"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                      tabIndex="-1"
                      id="manicura-menu-3"
                      onClick={() => setMenuOpen(false)}
                    >
                      Registro
                    </a>
                    <a
                      href="/login"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                      tabIndex="-1"
                      id="manicura-menu-4"
                      onClick={() => setMenuOpen(false)}
                    >
                      Acceso
                    </a>
                  </div>
                </div>
              )}
            </>
          ) : (
            <nav className="space-x-4">
              <a href="#features" className="hover:underline">
                Servicios
              </a>
              <a href="#about" className="hover:underline">
                Nosotros
              </a>
              <a href="#contact" className="hover:underline">
                Contacto
              </a>
              <a href="/register" className="hover:underline">
                Registro
              </a>
              <a href="/login" className="hover:underline">
                Acceso
              </a>
            </nav>
          )}
        </div>
      </header>

      <section className="bg-pink-100 py-20 text-center px-4">
        <h2 className="text-4xl font-bold mb-4">
          La plataforma ideal para estudios de manicuria
        </h2>
        <p className="text-lg mb-6 max-w-2xl mx-auto">
          Organizá turnos, administrá servicios y fidelizá a tus clientas con
          recordatorios automáticos y experiencias personalizadas.
        </p>

        <Button
          className="text-lg flex gap-2 items-center mx-auto"
          onClick={() =>
            window.location.assign(
              "https://api.whatsapp.com/send?phone=5491158888423&text=Hola,%20quiero%20saber%20m%C3%A1s%20sobre%20MANICURA"
            )
          }
        >
          <p>Reservá una demo</p>
          <svg
            stroke="currentColor"
            fill="currentColor"
            strokeWidth="0"
            viewBox="0 0 448 512"
            className="text-xl"
            height="1em"
            width="1em"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"></path>
          </svg>
        </Button>
      </section>

      <section id="features" className="py-20 bg-white px-4">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          <div>
            <h3 className="text-xl font-semibold mb-2">
              Turnos y recordatorios inteligentes
            </h3>
            <p>
              Automatizá la gestión de reservas y recordatorios para reducir
              ausencias y mantener tu agenda completa.
            </p>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-2">
              Catálogo de servicios y combos
            </h3>
            <p>
              Configurá fácilmente servicios individuales, packs y promos
              especiales para cada temporada.
            </p>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-2">
              Historial de clientas
            </h3>
            <p>
              Registrá estilos, colores y preferencias para ofrecer experiencias
              personalizadas en cada visita.
            </p>
          </div>
        </div>
      </section>

      <section id="about" className="bg-gray-100 py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-2xl font-bold mb-4">¿Quiénes somos?</h3>
          <p>
            Somos especialistas en belleza y tecnología que trabajan juntos para
            potenciar estudios de manicuria en toda Latinoamérica.
          </p>
          <p className="mt-4">
            Nuestro objetivo es que brindes un servicio impecable, sin perder
            tiempo en tareas administrativas.
          </p>
        </div>
      </section>

      <section id="contact" className="pt-20 pb-10 px-4">
        <div className="max-w-xl mx-auto text-center">
          <h3 className="text-2xl font-bold mb-4">Contacto</h3>
          <p className="mb-2">
            <a
              href={`mailto:${config.email}`}
              className="text-blue-600 underline"
            >
              {config.email}
            </a>
          </p>
          <p className="mb-2">
            <a
              href={`tel:+${config.phone}`}
              className="text-blue-600 underline"
            >
              {config.phone}
            </a>
          </p>
          <p className="mb-2">{config.address}</p>
          <div className="mt-6">
            <Button
              className="text-lg flex gap-2 items-center mx-auto"
              onClick={() =>
                window.location.assign(
                  "https://api.whatsapp.com/send?phone=5491158888423&text=Hola,%20quiero%20saber%20m%C3%A1s%20sobre%20MANICURA"
                )
              }
            >
              <p>Contactanos por WhatsApp</p>
              <svg
                stroke="currentColor"
                fill="currentColor"
                strokeWidth="0"
                viewBox="0 0 448 512"
                className="text-xl"
                height="1em"
                width="1em"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"></path>
              </svg>
            </Button>
          </div>

          <div className="flex justify-center gap-4 mt-10">
            <a
              href={config.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              Facebook
            </a>
            <a
              href={config.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              Instagram
            </a>
            <a
              href={config.twitter}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              Twitter
            </a>
          </div>
        </div>
      </section>

      <footer
        className={`text-white text-center p-4 bg-[${config.theme.colors.primaryColor}]`}
      >
        <p>
          &copy; {new Date().getFullYear()} {config.brand}. Todos los derechos
          reservados.
        </p>
      </footer>
    </main>
  );
}

export function Binapsis() {
  const navigate = useNavigate();
  const { t, i18n } = useBrandTranslation();
  const web =
    i18n.getResourceBundle(i18n.language, "translation")?.web || esWeb;
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDownloadDialog, setShowDownloadDialog] = useState(true);
  const [openFAQs, setOpenFAQs] = useState(new Set());
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);


  const iosStandalone = window.navigator.standalone === true;
  const standalone = window.matchMedia("(display-mode: standalone)").matches;
  const fullscreen = window.matchMedia("(display-mode: fullscreen)").matches;
  const minimalUI = window.matchMedia("(display-mode: minimal-ui)").matches;
  const isRunningAsInstalledPWA = iosStandalone || standalone || fullscreen || minimalUI;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    
    // Verificar al montar el componente
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const toggleFAQ = (index) => {
    const newOpenFAQs = new Set(openFAQs);
    if (newOpenFAQs.has(index)) {
      newOpenFAQs.delete(index);
    } else {
      newOpenFAQs.add(index);
    }
    setOpenFAQs(newOpenFAQs);
  };
  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-[#4a90d9] text-white sticky top-0 z-50 shadow-sm">
      {!isRunningAsInstalledPWA && (
        <DownloadAppDialog
          open={showDownloadDialog}
          brandName={config.brand}
          onCancel={() => setShowDownloadDialog(false)}
          onLearnMore={() => {
            setShowDownloadDialog(false);
            window.location.assign("/install");
          }}
        />
      )}
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3 bg-white rounded p-2">
            <img src={utils.getBrandLogoSrc()} alt={config.brand} width="200" height="48" className="h-10 w-auto object-contain" />
          </a>

          <div className="flex items-center gap-3">
            <LanguageSwitcher variant="light" />
          {isMobile ? (
            <>
              <button
                className="whitespace-nowrap rounded-lg text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 hover:bg-slate-100 hover:text-slate-900 h-10 w-10 flex items-center justify-center"
                type="button"
                id="radix-:R2pjta:"
                aria-haspopup="menu"
                aria-expanded="false"
                data-state="closed"
                onClick={() => {
                  setMenuOpen(!menuOpen);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-menu h-6 w-6"
                >
                  <line x1="4" x2="20" y1="12" y2="12"></line>
                  <line x1="4" x2="20" y1="6" y2="6"></line>
                  <line x1="4" x2="20" y1="18" y2="18"></line>
                </svg>
              </button>
              {menuOpen && (
                <div
                  className="absolute right-2 z-10 w-48 mr-[16px] mt-[237px] origin-top-right bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="radix-:R2pjta:"
                  tabIndex="-1"
                >
                  <div className="px-1 py-1" role="none">
                    <a
                      href="#caracteristicas"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                      tabIndex="-1"
                      id="radix-:R2pjta:0"
                      onClick={() => setMenuOpen(false)}
                    >
                      {web.nav.features}
                    </a>
                    <a
                      href="#nosotros"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                      tabIndex="-1"
                      id="radix-:R2pjta3:0"
                      onClick={() => setMenuOpen(false)}
                    >
                      {web.nav.about}
                    </a>
                    <a
                      href="#contacto"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                      tabIndex="-1"
                      id="radix-:R2pj2ta:0"
                      onClick={() => setMenuOpen(false)}
                    >
                      {web.nav.contact}
                    </a>
                    <a
                      href="#faqs"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                      tabIndex="-1"
                      id="radix-:R2pjfaqs:0"
                      onClick={() => setMenuOpen(false)}
                    >
                      {web.nav.faqs}
                    </a>
                    <a
                      href="/register"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                      tabIndex="-1"
                      id="radix-:R2dpjta:0"
                      onClick={() => setMenuOpen(false)}
                    >
                      {web.nav.register}
                    </a>
                    <a
                      href="/login"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                      tabIndex="-1"
                      id="radix-:R2pjcta:0"
                      onClick={() => setMenuOpen(false)}
                    >
                      {web.nav.access}
                    </a>
                  </div>
                </div>
              )}
            </>
          ) : (
            <nav className="hidden md:flex items-center gap-8">
              <a href="#caracteristicas" className="text-sm font-medium hover:text-white/80 transition-colors">
                {web.nav.features}
              </a>
              <a href="#nosotros" className="text-sm font-medium hover:text-white/80 transition-colors">
                {web.nav.about}
              </a>
              <a href="#contacto" className="text-sm font-medium hover:text-white/80 transition-colors">
                {web.nav.contact}
              </a>
              <a href="#faqs" className="text-sm font-medium hover:text-white/80 transition-colors">
                {web.nav.faqs}
              </a>
              <a href="/register" className="text-sm font-medium hover:text-white/80 transition-colors">
                {web.nav.register}
              </a>
              {/* </CHANGE> */}
              <Button variant="secondary" size="sm" className="font-medium bg-[#5bbaa5] text-white hover:bg-[#4da895]" onClick={() => navigate("/login")}>
                {web.nav.access}
              </Button>
            </nav>
          )}
          </div>

        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-[#e8f2fb] to-[#fdfefe] py-20 md:py-32">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-[#1e3a5f] mb-6 text-balance">
              {web.hero.title}
            </h1>
            <p className="text-lg md:text-xl text-[#6b7280] mb-8 text-pretty leading-relaxed max-w-2xl mx-auto">
              {t(
                "web.hero.subtitle",
                web.hero.subtitle,
                { brand: config.brand }
              )}
            </p>
            <Button
              size="lg"
              className="text-base font-semibold shadow-lg hover:shadow-xl transition-shadow bg-[#4a90d9] text-white hover:bg-[#3d7bc2]"
              onClick={() =>
                window.location.assign(
                  `https://api.whatsapp.com/send?phone=${config.whatsappNumber}&text=Hola,%20quiero%20saber%20mas%20sobre%${config.brand}`
                )
              }
            >
              <MessageCircle className="mr-2 h-5 w-5" />
              {web.hero.cta}
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="caracteristicas" className="py-20 bg-[#f0f9f9]">
          <h2 className="w-full text-3xl font-bold tracking-tight text-[#1e3a5f] mb-6 text-center">
              {web.features.title}
            </h2>
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
            <Card className="p-8 hover:shadow-lg transition-shadow border-2 bg-white border-[#e5e7eb]">
              <div className="bg-[#e8f2fb] w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                <Calendar className="h-7 w-7 text-[#4a90d9]" />
              </div>
              <h3 className="text-xl font-bold text-[#1e3a5f] mb-3">{web.features.appointments.title}</h3>
              <p className="text-[#6b7280] leading-relaxed">
                {web.features.appointments.description}
              </p>
            </Card>

            <Card className="p-8 hover:shadow-lg transition-shadow border-2 bg-white border-[#e5e7eb]">
              <div className="bg-[#d9f3ee] w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                <FileText className="h-7 w-7 text-[#5bbaa5]" />
              </div>
              <h3 className="text-xl font-bold text-[#1e3a5f] mb-3">{web.features.records.title}</h3>
              <p className="text-[#6b7280] leading-relaxed">
                {web.features.records.description}
              </p>
            </Card>

            <Card className="p-8 hover:shadow-lg transition-shadow border-2 bg-white border-[#e5e7eb]">
              <div className="bg-[#d9f3ee] w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                <Shield className="h-7 w-7 text-[#5bbaa5]" />
              </div>
              <h3 className="text-xl font-bold text-[#1e3a5f] mb-3">{web.features.security.title}</h3>
              <p className="text-[#6b7280] leading-relaxed">
                {web.features.security.description}
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Additional Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <Card className="p-8 md:p-10 hover:shadow-lg transition-shadow border-2 bg-white border-[#e5e7eb]">
              <div className="flex items-start gap-6 mb-6">
                <div className="bg-[#e8f2fb] w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Zap className="h-7 w-7 text-[#4a90d9]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-[#1e3a5f] mb-6">{web.features.extraTitle}</h3>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[#6b7280] leading-relaxed">
                    {web.features.extraItems.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <Check className="text-[#5bbaa5] mr-2 h-5 w-5 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="nosotros" className="py-20 bg-[#f9fafb]">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <p className="text-[#5bbaa5] font-semibold text-sm uppercase tracking-wider mb-3">{web.about.eyebrow}</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4 text-balance">{web.about.title}</h2>
              <div className="w-20 h-1 bg-[#5bbaa5] mx-auto rounded-full"></div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card className="p-8 bg-white border-2 hover:shadow-lg transition-shadow border-[#e5e7eb]">
                <div className="bg-[#e8f2fb] w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <Heart className="h-6 w-6 text-[#4a90d9]" />
                </div>
                <h3 className="text-xl font-bold text-[#1e3a5f] mb-3">{web.about.philosophy.title}</h3>
                <p className="text-[#6b7280] leading-relaxed">
                  {web.about.philosophy.body}
                </p>
              </Card>

              <Card className="p-8 bg-white border-2 hover:shadow-lg transition-shadow border-[#e5e7eb]">
                <div className="bg-[#d9f3ee] w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <Target className="h-6 w-6 text-[#5bbaa5]" />
                </div>
                <h3 className="text-xl font-bold text-[#1e3a5f] mb-3">{web.about.approach.title}</h3>
                <p className="text-[#6b7280] leading-relaxed">
                  {web.about.approach.body}
                </p>
              </Card>
            </div>

            <Card className="p-8 md:p-10 bg-gradient-to-br from-[#e8f2fb] to-[#d9f3ee] border-2 border-[#e5e7eb]">
              <div className="flex items-start gap-4 mb-6">
                <div className="bg-[#d9f3ee] w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users className="h-6 w-6 text-[#5bbaa5]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#1e3a5f] mb-3">{web.about.commitment.title}</h3>
                  <p className="text-[#6b7280] leading-relaxed text-pretty">
                    {web.about.commitment.body}
                  </p>
                </div>
              </div>
              <div className="pt-6 border-t border-[#e5e7eb]">
                <p className="text-center text-[#6b7280] font-medium italic">
                  {web.about.closing}
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section id="faqs" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <p className="text-[#5bbaa5] font-semibold text-sm uppercase tracking-wider mb-3">{web.faqs.eyebrow}</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4">{web.faqs.title}</h2>
              <div className="w-20 h-1 bg-[#5bbaa5] mx-auto rounded-full"></div>
            </div>

            <div className="space-y-4">
              {web.faqs.items.map((faq, index) => {
                const isOpen = openFAQs.has(index);
                return (
                  <Card key={index} className="bg-white border-2 border-[#e5e7eb] hover:shadow-lg transition-shadow overflow-hidden">
                    <button
                      onClick={() => toggleFAQ(index)}
                      className="w-full p-4 md:p-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <h3 className="text-lg font-bold text-[#1e3a5f] pr-4 flex-1">
                        {faq.question}
                      </h3>
                      <ChevronDown
                        className={`h-5 w-5 text-[#5bbaa5] flex-shrink-0 transition-transform duration-300 ${
                          isOpen ? "transform rotate-180" : ""
                        }`}
                      />
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        isOpen ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
                      }`}
                    >
                      <div className="px-6 md:px-8 pb-6 md:pb-8">
                        {faq.image && (
                          <div className="mt-4 mb-4">
                            <div className="bg-gray-100 rounded-lg p-4 text-center border-2 border-gray-200">
                              <img
                                src={process.env.PUBLIC_URL + faq.image}
                                alt={faq.imageAlt}
                                className="w-full h-auto object-cover rounded"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  e.target.nextSibling.style.display = "block";
                                }}
                              />
                              <div className="hidden text-gray-400 text-sm py-4">
                                {t("common.imageUnavailable", "Imagen no disponible")}
                              </div>
                            </div>
                          </div>
                        )}
                        <div className="text-[#6b7280] leading-relaxed pt-4">
                          {faq.answer.split("\n\n").map((paragraph, paragraphIndex) => (
                            <p key={paragraphIndex} className={paragraphIndex > 0 ? "mt-4" : undefined}>
                              {paragraph}
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}

              <Card className="p-6 md:p-8 bg-gradient-to-br from-[#e8f2fb] to-[#d9f3ee] border-2 border-[#5bbaa5]">
                <h3 className="text-xl font-bold text-[#1e3a5f] mb-3 text-center">{web.faqs.moreQuestions}</h3>
                <div className="text-center mt-4">
                  <Button
                    size="lg"
                    className="bg-[#4a90d9] text-white hover:bg-[#3d7bc2] font-semibold shadow-lg hover:shadow-xl transition-shadow"
                    onClick={() =>
                      window.location.assign(
                        `https://api.whatsapp.com/send?phone=${config.whatsappNumber}&text=Hola,%20tengo%20una%20consulta%20sobre%20${config.brand}`
                      )
                    }
                  >
                    <MessageCircle className="mr-2 h-5 w-5" />
                    {web.faqs.contactUs}
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="h-[500px] w-full">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d7520.853628065185!2d-58.4238032748296!3d-34.578714530467046!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x95bcb59c771eb933%3A0x6b3113b596d78c69!2sPalermo%2C%20Cdad.%20Aut%C3%B3noma%20de%20Buenos%20Aires!5e0!3m2!1ses!2sar!4v1765108578119!5m2!1ses!2sar"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </section>

      {/* Contact Section */}
      <section id="contacto" className="py-20 bg-[#5bbaa5] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="mb-12">
              <p className="text-white/80 font-semibold text-sm uppercase tracking-wider mb-3">
                {web.contact.eyebrow}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">{web.contact.title}</h2>
              <div className="w-20 h-1 bg-white/30 mx-auto rounded-full"></div>
            </div>
            <div className="space-y-3 mb-8">
              <p className="text-lg">
                <a href={`mailto:${config.email}`} className="hover:underline font-medium">
                  {config.email}
                </a>
              </p>
              <p className="text-lg">
                <a href={`tel:+${config.phone}`} className="hover:underline font-medium">
                  {config.phone}
                </a>
              </p>
              <p className="text-lg font-medium">{config.address}</p>
            </div>
            <Button
              size="lg"
              variant="secondary"
              className="bg-white text-[#5bbaa5] font-semibold shadow-lg hover:shadow-xl transition-shadow hover:bg-[#f9fafb]"
              onClick={() =>
                window.location.assign(
                  `https://api.whatsapp.com/send?phone=${config.whatsappNumber}&text=Hola,%20quiero%20saber%20mas%20sobre%${config.brand}`
                )
              }
            >
              <MessageCircle className="mr-2 h-5 w-5" />
              {web.hero.cta}
            </Button>
            <div className="flex justify-center gap-6 mt-8">
              {
                config.socialMedia.facebook !== "" && (
                  <a href={config.socialMedia.facebook} className="text-white/80 hover:text-white transition-colors font-medium">
                    Facebook
                  </a>
                )
              }
              {
                config.socialMedia.instagram !== "" && (
                  <a href={config.socialMedia.instagram} className="text-white/80 hover:text-white transition-colors font-medium">
                    Instagram
                  </a>
                )
              }
              {
                config.socialMedia.twitter !== "" && (
                  <a href={config.socialMedia.twitter} className="text-white/80 hover:text-white transition-colors font-medium">
                    Twitter
                  </a>
                )
              }
              {
                config.socialMedia.linkedin !== "" && (
                  <a href={config.socialMedia.linkedin} className="text-white/80 hover:text-white transition-colors font-medium">
                    LinkedIn
                  </a>
                )
              }
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#4a90d9] text-white py-6">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm">© {new Date().getFullYear()} {config.brand}. {t("common.allRightsReserved", "Todos los derechos reservados.")}</p>
        </div>
      </footer>
    </div>
  )
}